import React, { useEffect, useState } from "react";
import {
    Button,
    MenuItem,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Box,
    IconButton,
    CircularProgress,
    Typography
} from "@mui/material";
import { Close as CloseIcon, Download as DownloadIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { ColumnConfig } from "../types/ColumnConfig";
import { TablePagination } from "../types/Pagination";
import Search from "../components/SearchInput";
import DateInput from "../components/DateInput";
import { formatToDDMMYYYY } from "../utils/Validation";
import "./Tickets.css";
import "./Common.css";
import "rsuite/dist/rsuite.min.css";
import DropdownSearch from "../components/DropDown";
import { getTicketList, updateTicketStatus, getTicketListDocument, getUserListForFilters } from "../services/apiService";
import CustomTable from "../components/CustomTable";
import ExporterButton from "../components/ExportButton";

interface UpdateStatusDialogProps {
    open: boolean;
    currentStatus: string;
    currentComment: string;
    ticketId: number;
    onClose: () => void;
    onUpdate: (id: number, status: string, comment: string) => Promise<void>;
    isUpdating: boolean;
    isViewOnly: boolean;
}

const UpdateStatusDialog: React.FC<UpdateStatusDialogProps> = ({
    open,
    currentStatus,
    currentComment,
    ticketId,
    onClose,
    onUpdate,
    isUpdating,
    isViewOnly
}) => {
    const [selectedStatus, setSelectedStatus] = useState(currentStatus);
    const [initialStatus, setInitialStatus] = useState(currentStatus); // Track initial status
    const [comment, setComment] = useState(currentComment || '');

    useEffect(() => {
        if (open) {
            setSelectedStatus(currentStatus);
            setInitialStatus(currentStatus); // Set initial status when dialog opens
            setComment(currentComment || '');
        }
    }, [open, currentStatus, currentComment]);

    const getAvailableStatusOptions = (currentStatus: string, isViewOnly: boolean) => {
        if (isViewOnly) {
            const statusLabel = currentStatus === 'in-process' ? 'In Process' :
                currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1);
            return [
                { value: currentStatus, label: statusLabel }
            ];
        }

        switch (currentStatus) {
            case 'pending':
                return [
                    { value: 'pending', label: 'Pending' },
                    { value: 'in-process', label: 'In Process' }
                ];
            case 'in-process':
                return [
                    { value: 'in-process', label: 'In Process' },
                    { value: 'closed', label: 'Closed' }
                ];
            case 'closed':
                return [
                    { value: 'closed', label: 'Closed' }
                ];
            default:
                return [
                    { value: 'pending', label: 'Pending' },
                    { value: 'in-process', label: 'In Process' },
                    { value: 'closed', label: 'Closed' }
                ];
        }
    };

    const statusOptions = getAvailableStatusOptions(currentStatus, isViewOnly);

    const handleSubmit = async () => {
        if (isViewOnly) {
            onClose();
            return;
        }

        if (!selectedStatus) {
            toast.error('Please select a status');
            return;
        }

        if (currentStatus === 'pending' && !['pending', 'in-process'].includes(selectedStatus)) {
            toast.error('Pending tickets can only be moved to In Process');
            return;
        }

        if (currentStatus === 'in-process' && !['in-process', 'closed'].includes(selectedStatus)) {
            toast.error('In Process tickets can only be moved to Closed');
            return;
        }

        if (currentStatus === 'closed' && selectedStatus !== 'closed') {
            toast.error('Closed tickets cannot be changed');
            return;
        }

        await onUpdate(ticketId, selectedStatus, comment);
    };

    const handleCancel = () => {
        onClose();
    };

    // Disable submit button if status hasn't changed from initial status
    const isSubmitDisabled = isUpdating || isViewOnly || selectedStatus === initialStatus;

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isViewOnly ? 'View Ticket Status' : 'Update Ticket Status'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={selectedStatus}
                            label="Status"
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            disabled={isUpdating || isViewOnly}
                        >
                            {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Resolution Notes / Comment"
                        multiline
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={isViewOnly ? "No comments available" : "Enter your comments about the status update..."}
                        disabled={isUpdating || isViewOnly}
                        fullWidth
                        helperText={isViewOnly ? "Current resolution notes" : "You can add or update comments along with status change"}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleCancel}
                    disabled={isUpdating}
                    color="inherit"
                >
                    {isViewOnly ? 'Close' : 'Cancel'}
                </Button>
                {!isViewOnly && (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitDisabled}
                    >
                        {isUpdating ? 'Updating...' : 'Submit'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

interface DocumentPreviewDialogProps {
    open: boolean;
    ticketId: number | null;
    onClose: () => void;
}

const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
    open,
    ticketId,
    onClose
}) => {
    const [documentUrl, setDocumentUrl] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (open && ticketId) {
            fetchDocument();
        } else {
            setDocumentUrl('');
            setError('');
        }
    }, [open, ticketId]);

    const fetchDocument = async () => {
        if (!ticketId) return;

        setLoading(true);
        setError('');

        try {
            const response = await getTicketListDocument({ id: ticketId });

            if (response?.responseCode === 200 && response?.data?.url) {
                setDocumentUrl(response?.data?.url);
            } else {
                setError('Failed to fetch document URL');
                toast.error('Failed to fetch document');
            }
        } catch (error: any) {
            console.error('Error fetching document:', error);
            setError('Error loading document');
            toast.error('Error loading document');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (documentUrl) {
            const link = document.createElement('a');
            link.href = documentUrl;
            link.download = `ticket-${ticketId}-document`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const getFileExtension = (url: string): string => {
        try {
            const urlParts = url.split('?')[0];
            const extension = urlParts.split('.').pop()?.toLowerCase() || '';
            return extension;
        } catch {
            return '';
        }
    };

    const renderFilePreview = () => {
        if (loading) {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px'
                }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading document...</Typography>
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    flexDirection: 'column'
                }}>
                    <Typography color="error" variant="h6">
                        {error}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={fetchDocument}
                        sx={{ mt: 2 }}
                    >
                        Retry
                    </Button>
                </Box>
            );
        }

        if (!documentUrl) {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px'
                }}>
                    <Typography>No document available</Typography>
                </Box>
            );
        }

        const fileExtension = getFileExtension(documentUrl);

        if (fileExtension === 'pdf') {
            return (
                <Box sx={{ width: '100%', height: '600px' }}>
                    <iframe
                        src={documentUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="Document Preview"
                    />
                </Box>
            );
        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxHeight: '600px',
                    overflow: 'auto'
                }}>
                    <img
                        src={documentUrl}
                        alt="Document Preview"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '600px',
                            objectFit: 'contain'
                        }}
                    />
                </Box>
            );
        } else {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    flexDirection: 'column'
                }}>
                    <Typography variant="h6" gutterBottom>
                        Preview not available for this file type
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        File type: {fileExtension.toUpperCase()}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        sx={{ mt: 2 }}
                    >
                        Download File
                    </Button>
                </Box>
            );
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '80vh',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1
            }}>
                <Typography variant="h6">
                    Document Preview - Ticket #{ticketId}
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{ color: 'grey.500' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {renderFilePreview()}
            </DialogContent>

            {documentUrl && !loading && !error && (
                <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
                    <Button onClick={onClose} color="inherit">
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        color="primary"
                    >
                        Download
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

const Tickets = () => {
    const [filters, setFilters] = useState({
        userIds: "",
        category: "",
        status: "",
        fromDate: null,
        toDate: null,
    });

    const statusDropdown = [
        { label: "Pending", value: "pending" },
        { label: "In Process", value: "in-process" },
        { label: "Closed", value: "closed" },
    ];

    const categoryDropdown = [
        { label: "Redeem Issue", value: "redeem_issue" },
        { label: "Points Issue", value: "points_issue" },
        { label: "Scan Issue", value: "scan_issue" },
    ];

    const [data, setData] = useState<any[]>([]);
    const [paginationData, setPaginationData] = useState<TablePagination>({
        limit: 10,
        skip: 0,
    });
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [userList, setUserList] = useState<{ userId: number; userName: string }[]>([]);
    const [userLoading, setUserLoading] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<number | null>(null);

    const [updateStatusDialog, setUpdateStatusDialog] = useState({
        open: false,
        ticketId: 0,
        currentStatus: '',
        currentComment: '',
        isViewOnly: false
    });

    const [documentPreviewDialog, setDocumentPreviewDialog] = useState({
        open: false,
        ticketId: null as number | null
    });

    useEffect(() => {
        const fetchUserList = async () => {
            setUserLoading(true);
            try {
                const response = await getUserListForFilters();
                if (response?.data) {
                    setUserList(response?.data);
                }
            } catch (error) {
                console.error("Error fetching user list", error);
                toast.error("Failed to load user list");
            } finally {
                setUserLoading(false);
            }
        };
        fetchUserList();

        const handler = setTimeout(() => {
            const isUserIdValid = filters.userIds === "" || filters.userIds.length >= 1;
            const areDatesValid =
                (filters.fromDate && filters.toDate) ||
                (!filters.fromDate && !filters.toDate);

            if (isUserIdValid && areDatesValid) {
                loadData();
            }
        }, 800);
        return () => clearTimeout(handler);
    }, [filters, paginationData]);

    const userDropdownOptions = userList?.map((user) => ({
        label: user.userName,
        value: user.userId.toString(),
    }));

    const loadData = async () => {
        setLoading(true);
        try {
            const apiParams: any = {
                search: "",
            };

            if (filters.userIds.trim()) {
                const userIdsArray = filters.userIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
                if (userIdsArray?.length === 1) {
                    apiParams.userId = userIdsArray[0];
                } else if (userIdsArray?.length > 1) {
                    apiParams.userId = userIdsArray[0];
                }
            }

            if (filters.category) {
                apiParams.category = filters.category;
            }

            if (filters.status) {
                apiParams.status = filters.status;
            }

            if (filters.fromDate) {
                apiParams.fromDate = formatToDDMMYYYY(filters.fromDate);
            }
            if (filters.toDate) {
                apiParams.toDate = formatToDDMMYYYY(filters.toDate);
            }

            apiParams.limit = paginationData.limit;
            apiParams.skip = paginationData.skip;

            const response = await getTicketList(apiParams);

            if (response?.data) {
                setData(response?.data);
                setTotalRows(response?.totalCount || response?.data?.length);
            } else {
                setData([]);
                setTotalRows(0);
            }
        } catch (error) {
            console.error("Error loading ticket data", error);
            toast.error("Error loading ticket data");
            setData([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    };

    const canUpdateStatus = (status: string): boolean => {
        const statusLower = status.toLowerCase();
        return statusLower === 'pending' || statusLower === 'in-process';
    };

    const handleOpenUpdateDialog = (ticketId: number, currentStatus: string, currentComment: string) => {
        const isViewOnly = !canUpdateStatus(currentStatus);
        setUpdateStatusDialog({
            open: true,
            ticketId,
            currentStatus,
            currentComment: isViewOnly ? (currentComment || '') : '',
            isViewOnly
        });
    };

    const handleCloseUpdateDialog = () => {
        setUpdateStatusDialog({
            open: false,
            ticketId: 0,
            currentStatus: '',
            currentComment: '',
            isViewOnly: false
        });
    };

    const handleOpenDocumentPreview = (ticketId: number) => {
        setDocumentPreviewDialog({
            open: true,
            ticketId
        });
    };

    const handleCloseDocumentPreview = () => {
        setDocumentPreviewDialog({
            open: false,
            ticketId: null
        });
    };

    const handleUpdateTicketStatus = async (id: number, status: string, comment: string) => {
        setIsUpdatingStatus(id);

        try {
            const body = {
                status: status,
                resolutionNotes: comment.trim() || `Ticket ${status} by admin`
            };

            const response = await updateTicketStatus(id, body);

            if (response?.responseCode === 200 || response?.code === 200) {
                toast.success("Ticket status updated successfully");
                loadData();
                handleCloseUpdateDialog();
            } else {
                const errorMessage = response?.responseMessage || response?.message || "Failed to update ticket status";
                toast.error(errorMessage);
                console.error('Update failed:', response);
            }
        } catch (error: any) {
            if (error?.response?.data) {
                const errorData = error?.response?.data;

                let parsedError;
                try {
                    parsedError = typeof errorData === 'string' ? JSON.parse(errorData) : errorData;
                } catch {
                    parsedError = errorData;
                }

                const errorMessage = parsedError?.responseMessage || parsedError?.message || parsedError || "Error updating ticket status";
                toast.error(`API Error: ${errorMessage}`);
            } else if (error?.message) {
                if (error.message.includes('Network Error')) {
                    toast.error("Network Error: Please check your internet connection");
                } else if (error.message.includes('timeout')) {
                    toast.error("Request Timeout: Please try again");
                } else if (error.message.includes('404')) {
                    toast.error("API Endpoint Not Found: Please check the API configuration");
                } else if (error.message.includes('500')) {
                    toast.error("Server Error: Please try again later");
                } else {
                    toast.error(`Error: ${error.message}`);
                }
            } else {
                toast.error("Unknown error updating ticket status");
            }
        } finally {
            setIsUpdatingStatus(null);
        }
    };

    const columns: ColumnConfig[] = [
        {
            field: "fullName",
            headerName: "Name",
            width: 160,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: "category",
            headerName: "Category",
            width: 140,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const category = params.value?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                return category || '-';
            }
        },
        {
            field: "referenceNumber",
            headerName: "Reference Number",
            width: 200,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: "description",
            headerName: "Description",
            width: 180,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        maxWidth: '170px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} title={params.value || ''}>
                        {params.value || '-'}
                    </div>
                );
            }
        },
        {
            field: "filePath",
            headerName: "File",
            width: 140,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return params.value ? (
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenDocumentPreview(params.row.id)}
                        sx={{ fontSize: '11px', mb: 3, }}
                    >
                        View
                    </Button>
                ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No file</span>
                );
            }
        },
        {
            field: "status",
            headerName: "Status",
            width: 140,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const status = params.value?.toLowerCase();
                return (
                    <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor:
                            status === 'pending' ? '#fff3cd' :
                                status === 'in-process' ? '#d4edda' :
                                    status === 'closed' ? '#d1ecf1' : 'transparent',
                        color:
                            status === 'pending' ? '#856404' :
                                status === 'in-process' ? '#155724' :
                                    status === 'closed' ? '#0c5460' : 'inherit',
                        fontSize: '12px',
                        fontWeight: '500'
                    }}>
                        {params.value?.charAt(0).toUpperCase() + params.value?.slice(1).replace('-', ' ')}
                    </span>
                );
            }
        },
        {
            field: "createdAt",
            headerName: "Upload Date",
            width: 140,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return params.value ? formatToDDMMYYYY(params.value) : '-';
            }
        },
        {
            field: "resolutionNotes",
            headerName: "Resolution Notes",
            width: 180,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        maxWidth: '170px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} title={params.value || ''}>
                        {params.value || '-'}
                    </div>
                );
            }
        },
        {
            field: "closedByFullName",
            headerName: "Updated By",
            width: 140,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        maxWidth: '170px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} title={params.value || ''}>
                        {params.value || '-'}
                    </div>
                );
            }
        },
        {
            field: "action",
            headerName: "Action",
            width: 140,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const canUpdate = canUpdateStatus(params.row.status);
                return (
                    <Button
                        variant="contained"
                        color={canUpdate ? "primary" : "info"}
                        size="small"
                        onClick={() => handleOpenUpdateDialog(
                            params.row.id,
                            params.row.status,
                            params.row.resolutionNotes
                        )}
                        disabled={isUpdatingStatus === params.row.id}
                        sx={{
                            fontSize: '12px',
                            minWidth: '110px',
                            mb: 3,
                        }}
                    >
                        {isUpdatingStatus === params.row.id 
                            ? 'Updating...' 
                            : canUpdate 
                                ? 'Update Status' 
                                : 'View Status'}
                    </Button>
                );
            },
        }
    ];

    const handlePaginationChange = async (e: any) => {
        const paginationData: TablePagination = {
            limit: e.pageSize,
            skip: e.page * e.pageSize,
        };
        setPaginationData(paginationData);
    };

    const handleInput = (e: string, type: string) => {
        setFilters((prev) => ({ ...prev, [type]: e }));
        if (type !== 'limit' && type !== 'skip') {
            setPaginationData({ limit: 10, skip: 0 });
        }
    };

    const fileExporter = async () => {
        try {
            const apiParams: any = {
                search: "",
            };

            if (filters.userIds.trim()) {
                const userIdsArray = filters.userIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
                if (userIdsArray?.length === 1) {
                    apiParams.userId = userIdsArray[0];
                } else if (userIdsArray?.length > 1) {
                    apiParams.userId = userIdsArray[0];
                }
            }

            if (filters.category) {
                apiParams.category = filters.category;
            }

            if (filters.status) {
                apiParams.status = filters.status;
            }

            if (filters.fromDate) {
                apiParams.fromDate = formatToDDMMYYYY(filters.fromDate);
            }
            if (filters.toDate) {
                apiParams.toDate = formatToDDMMYYYY(filters.toDate);
            }

            apiParams.limit = totalRows || 10000;
            apiParams.skip = 0;

            const response = await getTicketList(apiParams);

            if (response?.data && response?.data.length > 0) {
                toast.success("Exported successfully.");
                return response.data;
            } else {
                toast.error("No data available to export.");
                return [];
            }
        } catch (error) {
            toast.error("Failed to fetch export data.");
            return [];
        }
    };

    return (
        <div>
            <div
                style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                }}
            >
                <span className="redemption-title">Manage Tickets</span>
                <div
                    className="redemption-header"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        width: "100%",
                    }}
                >
                    <div style={{ display: "flex", gap: "50px", flexWrap: "wrap", marginTop: 10, marginBottom: 40 }}>
                        <DropdownSearch
                            searchTxt={filters.userIds}
                            handleChange={(e: any) => handleInput(e, "userIds")}
                            placeholder="Select User"
                            label="User Search"
                            data={userDropdownOptions}
                            searchable={true}
                            cleanable={true}
                        />
                        <DropdownSearch
                            searchTxt={filters.category}
                            handleChange={(e: any) => handleInput(e, "category")}
                            placeholder="Select Category"
                            label="Category"
                            data={categoryDropdown}
                            searchable={true}
                            cleanable={true}
                        />
                        <DropdownSearch
                            searchTxt={filters.status}
                            handleChange={(e: any) => handleInput(e, "status")}
                            placeholder="Select a Status"
                            label="Status"
                            data={statusDropdown}
                            searchable={true}
                            cleanable={true}
                        />
                        <DateInput
                            handleInput={(e) => handleInput(e, "fromDate")}
                            searchTxt={filters.fromDate}
                            placeholder="Select From Date"
                            label="From Date"
                            minDate={filters.toDate}
                        />
                        <DateInput
                            handleInput={(e) => handleInput(e, "toDate")}
                            searchTxt={filters.toDate}
                            placeholder="Select To Date"
                            label="To Date"
                            maxDate={filters.fromDate}
                        />
                    </div>
                </div>

        <div className="table-container">
                    <div style={{ display: "flex", justifyContent: "flex-start", padding: "10px 10px 0px 10px", backgroundColor: "#fff" }}>
                        <ExporterButton exporter={fileExporter} reportName="Tickets Report" />
                    </div>
                    <CustomTable
                        editMode={"row"}
                        data={data}
                        columns={columns}
                        getRowId={(ele: any) => ele?.id}
                        loading={loading}
                    />
                </div>

                {/* Update Status Dialog */}
                <UpdateStatusDialog
                    open={updateStatusDialog.open}
                    currentStatus={updateStatusDialog.currentStatus}
                    currentComment={updateStatusDialog.currentComment}
                    ticketId={updateStatusDialog.ticketId}
                    onClose={handleCloseUpdateDialog}
                    onUpdate={handleUpdateTicketStatus}
                    isUpdating={isUpdatingStatus !== null}
                    isViewOnly={updateStatusDialog.isViewOnly}
                />

                {/* Document Preview Dialog */}
                <DocumentPreviewDialog
                    open={documentPreviewDialog.open}
                    ticketId={documentPreviewDialog.ticketId}
                    onClose={handleCloseDocumentPreview}
                />
            </div>
        </div>
    );
};

export default Tickets;